
const formatLastSeen = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));

  if (minutes < 5) return 'Online ahora';
  if (minutes < 60) return `Online hace ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `Online hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `Online hace ${days} ${days === 1 ? 'día' : 'días'}`;
  }

  const weeks = Math.floor(days / 7);
  if (days < 30) {
    return `Online hace ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
  }

  const months = Math.floor(days / 30);
  return `Online hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
};

const firstNameOnly = (value) => {
  const clean = String(value || '').trim();
  return clean ? clean.split(/\s+/)[0] : 'Cuidador';
};


const formatShortAddress = (data) => {
  const addr = data?.address || {};

  const calle =
    addr.road ||
    addr.pedestrian ||
    addr.footway ||
    addr.neighbourhood ||
    addr.suburb ||
    addr.hamlet ||
    addr.locality ||
    '';

  const comuna =
    addr.city ||
    addr.town ||
    addr.village ||
    addr.municipality ||
    addr.county ||
    '';

  return {
    shortAddress: [calle, comuna].filter(Boolean).join(', ') || data?.display_name || 'Mi ubicación',
    comuna
  };
};

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MapPin, Star, Shield, Navigation, Search, X, ChevronRight, Dog, Crown } from 'lucide-react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import api from '@/lib/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: -33.4489, lng: -70.6693 };
const DEFAULT_ZOOM = 12;
const mapStyles = [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }];

const SERVICE_TABS = [
  { id: 'paseo', label: 'Paseo' },
  { id: 'cuidado', label: 'Cuidado' },
  { id: 'daycare', label: 'Daycare' },
];

const PET_SIZE_LABELS = { pequeno: 'Pequeno', mediano: 'Mediano', grande: 'Grande' };

const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL}${path}`;
};

const getProviderMainImage = (provider) => {
  if (provider?.profile_photo) return getPhotoUrl(provider.profile_photo);
  if (provider?.photos?.[0]) return getPhotoUrl(provider.photos[0]);
  return null;
};

const sortProviders = (list) => {
  return [...list].sort((a, b) => {
    if (!!a.is_subscribed !== !!b.is_subscribed) {
      return a.is_subscribed ? -1 : 1;
    }

    const ad = typeof a.distance_km === 'number' ? a.distance_km : 999999;
    const bd = typeof b.distance_km === 'number' ? b.distance_km : 999999;

    return ad - bd;
  });
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchAddress, setSearchAddress] = useState(searchParams.get('comuna') || '');
  const [activeService, setActiveService] = useState(searchParams.get('service') || '');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [hoveredProvider, setHoveredProvider] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [searchRadius] = useState(10);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [isMapSearchActive, setIsMapSearchActive] = useState(true);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const boundsTimeoutRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: LIBRARIES
  });

  useEffect(() => {
    const rawDates = searchParams.get('dates');

    if (rawDates) {
      const parsed = rawDates
        .split(',')
        .map(d => d.trim())
        .filter(Boolean)
        .map(d => new Date(d))
        .filter(d => !Number.isNaN(d.getTime()));

      if (parsed.length > 0) {
        setSelectedDates(parsed);
      }
    }

    const from = searchParams.get('from');
    const to = searchParams.get('to');

    if (from) {
      const fromDate = new Date(from);
      const toDate = to ? new Date(to) : undefined;

      if (!Number.isNaN(fromDate.getTime())) {
        setDateRange({
          from: fromDate,
          to: toDate && !Number.isNaN(toDate.getTime()) ? toDate : undefined
        });
      }
    }
  }, []);


  useEffect(() => {
    loadProviders();
  }, [activeService]);

  useEffect(() => {
    const urlComuna = searchParams.get('comuna');
    const urlLat = searchParams.get('lat');
    const urlLng = searchParams.get('lng');
    const source = searchParams.get('source');

    if (source === 'location' && urlLat && urlLng) {
      const loc = { lat: parseFloat(urlLat), lng: parseFloat(urlLng) };
      setUserLocation(loc);
      setMapCenter(loc);
      setMapZoom(14);
      setSearchAddress(urlComuna || 'Mi ubicación');

      setTimeout(() => {
        if (mapRef.current) mapRef.current.panTo(loc);
      }, 400);

      return;
    }

    if (urlComuna) {
      setSearchAddress(urlComuna);
      setUserLocation(null);
      return;
    }

    const savedLat = localStorage.getItem('ucan_user_lat');
    const savedLng = localStorage.getItem('ucan_user_lng');
    const savedAddress = localStorage.getItem('ucan_user_address');

    if (savedLat && savedLng) {
      const loc = { lat: parseFloat(savedLat), lng: parseFloat(savedLng) };
      setUserLocation(loc);
      setMapCenter(loc);
      setMapZoom(14);
      setSearchAddress(savedAddress || 'Mi ubicación');
      return;
    }

    getCurrentLocation();
  }, []);

  useEffect(() => {
    if (userLocation && providers.length > 0) {
      filterProvidersByLocation(userLocation, searchRadius);
    }
  }, [providers, userLocation, activeService]);

  useEffect(() => {
    const source = searchParams.get('source');
    const urlComuna = searchParams.get('comuna');

    if (source !== 'comuna' || !urlComuna || !isLoaded || !window.google?.maps?.Geocoder) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { address: `${urlComuna}, Región Metropolitana, Chile`, region: 'cl' },
      (results, status) => {
        if (status === 'OK' && results[0]) {
          const newCenter = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };

          setMapCenter(newCenter);
          setMapZoom(13);
          filterProvidersByLocation(newCenter, searchRadius);

          if (mapRef.current) {
            mapRef.current.panTo(newCenter);
            mapRef.current.setZoom(13);
          }
        }
      }
    );
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded && inputRef.current && !autocompleteRef.current && window.google?.maps?.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'cl' },
        fields: ['geometry', 'formatted_address']
      });

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
          const newCenter = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          let nextZoom = 13;

          const placeTypes = place.types || [];
          if (placeTypes.includes('street_address') || placeTypes.includes('premise')) {
            nextZoom = 15;
          } else if (placeTypes.includes('route')) {
            nextZoom = 14;
          } else if (
            placeTypes.includes('locality') ||
            placeTypes.includes('sublocality') ||
            placeTypes.includes('administrative_area_level_3')
          ) {
            nextZoom = 13;
          } else if (
            placeTypes.includes('administrative_area_level_1') ||
            placeTypes.includes('country')
          ) {
            nextZoom = 9;
          }

          setMapCenter(newCenter);
          setMapZoom(nextZoom);
          setSearchAddress(place.formatted_address || '');
          filterProvidersByLocation(newCenter, searchRadius);
          if (mapRef.current) {
            mapRef.current.panTo(newCenter);
            mapRef.current.setZoom(nextZoom);
            setTimeout(() => {
              const bounds = mapRef.current.getBounds();
              if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                loadProviders({
                  north: ne.lat(),
                  east: ne.lng(),
                  south: sw.lat(),
                  west: sw.lng()
                });
              }
            }, 700);
          }
        }
      });
    }
  }, [isLoaded, searchRadius]);

  const loadProviders = async (bounds = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeService) params.set('service_type', activeService);

      let datesStr = '';
      if (activeService === 'alojamiento' && dateRange.from) {
        const dates = [];
        let d = new Date(dateRange.from);
        const end = dateRange.to || dateRange.from;
        while (d <= end) {
          dates.push(d.toISOString().slice(0, 10));
          d = new Date(d.getTime() + 86400000);
        }
        datesStr = dates.join(',');
      } else if (selectedDates.length > 0) {
        datesStr = selectedDates.map(d => d.toISOString().slice(0, 10)).join(',');
      }
      if (datesStr) params.set('dates', datesStr);

      if (bounds) {
        params.set('bounds_south', bounds.south.toString());
        params.set('bounds_west', bounds.west.toString());
        params.set('bounds_north', bounds.north.toString());
        params.set('bounds_east', bounds.east.toString());
      }

      const response = await api.get(`/providers?${params.toString()}`);

      const sortedData = sortProviders(response.data);

      setProviders(sortedData);
      setFilteredProviders(sortedData);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBoundsChanged = useCallback(() => {
    if (!mapRef.current || !isMapSearchActive) return;

    if (boundsTimeoutRef.current) {
      clearTimeout(boundsTimeoutRef.current);
    }

    boundsTimeoutRef.current = setTimeout(() => {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const newBounds = {
          north: ne.lat(),
          east: ne.lng(),
          south: sw.lat(),
          west: sw.lng()
        };
        setMapBounds(newBounds);
        loadProviders(newBounds);
      }
    }, 500);
  }, [isMapSearchActive, activeService, dateRange, selectedDates]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const filterProvidersByLocation = useCallback((center, radius) => {
    const filtered = providers
      .map(provider => {
        if (provider.latitude && provider.longitude) {
          const distance = calculateDistance(center.lat, center.lng, provider.latitude, provider.longitude);
          return { ...provider, distance_km: Math.round(distance * 10) / 10 };
        }
        return { ...provider, distance_km: null };
      })
      .sort((a, b) => {
        if (a.distance_km === null) return 1;
        if (b.distance_km === null) return -1;
        return a.distance_km - b.distance_km;
      });

    setFilteredProviders(
      sortProviders(
        activeService
          ? filtered.filter(p => p.services?.some(s => s.service_type === activeService))
          : filtered
      )
    );
  }, [providers, activeService]);


  const getProviderProfileUrl = (providerId) => {
    const dates = (selectedDates || [])
      .map(d => {
        if (d instanceof Date) return d.toISOString().slice(0, 10);
        return String(d).slice(0, 10);
      })
      .filter(Boolean);

    return dates.length
      ? `/provider/${providerId}?dates=${dates.join(',')}`
      : `/provider/${providerId}`;
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Tu navegador no soporta geolocalización');
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        setUserLocation(newLocation);
        setMapCenter(newLocation);
        setMapZoom(14);
        setSearchAddress('Mi ubicación');

        localStorage.setItem('ucan_user_lat', newLocation.lat);
        localStorage.setItem('ucan_user_lng', newLocation.lng);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLocation.lat}&lon=${newLocation.lng}&zoom=18&addressdetails=1`
          );
          const data = await res.json();

          if (data?.display_name) {
            const { shortAddress, comuna } = formatShortAddress(data);

            setSearchAddress(shortAddress);
            localStorage.setItem('ucan_user_address', shortAddress);

            if (comuna) localStorage.setItem('ucan_user_comuna', comuna);
          }
        } catch (error) {
          console.error('Error obteniendo dirección:', error);
        }

        filterProvidersByLocation(newLocation, searchRadius);

        if (mapRef.current) {
          mapRef.current.panTo(newLocation);
          mapRef.current.setZoom(14);
        }

        setLocationLoading(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 600000
      }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProviders();

    if (searchAddress.trim() && isLoaded && window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: searchAddress, region: 'cl' }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const newCenter = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };
          setMapCenter(newCenter);
          setMapZoom(13);
          filterProvidersByLocation(newCenter, searchRadius);
          if (mapRef.current) {
            mapRef.current.panTo(newCenter);
            setTimeout(() => {
              const bounds = mapRef.current.getBounds();
              if (bounds) {
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                loadProviders({
                  north: ne.lat(),
                  east: ne.lng(),
                  south: sw.lat(),
                  west: sw.lng()
                });
              }
            }, 700);
          }
        }
      });
    }
  };

  const clearSearch = () => {
    setSearchAddress('');
    setUserLocation(null);
    setFilteredProviders(providers);
    setMapCenter(DEFAULT_CENTER);
    setMapZoom(DEFAULT_ZOOM);
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const createMarkerIcon = (isHighlighted) => {
    if (!isLoaded || !window.google?.maps?.Size || !window.google?.maps?.Point) {
      return undefined;
    }

    const svg = isHighlighted
      ? `<svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0C8.95 0 0 8.95 0 20c0 15 20 30 20 30s20-15 20-30C40 8.95 31.05 0 20 0z" fill="#E6202E"/>
          <circle cx="20" cy="18" r="8" fill="white"/>
        </svg>`
      : `<svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0z" fill="#E6202E"/>
          <circle cx="16" cy="14" r="6" fill="white"/>
        </svg>`;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: isHighlighted
        ? new window.google.maps.Size(40, 50)
        : new window.google.maps.Size(32, 40),
      anchor: isHighlighted
        ? new window.google.maps.Point(20, 50)
        : new window.google.maps.Point(16, 40)
    };
  };

  const createUserLocationIcon = () => {
    if (!isLoaded || !window.google?.maps?.Size || !window.google?.maps?.Point) {
      return undefined;
    }

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#4285F4" stroke="white" stroke-width="3"/>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(24, 24),
      anchor: new window.google.maps.Point(12, 12)
    };
  };

  const getDateLabel = () => {
    if (activeService === 'alojamiento') {
      if (dateRange.from && dateRange.to) return `${format(dateRange.from, 'dd MMM', { locale: es })} - ${format(dateRange.to, 'dd MMM', { locale: es })}`;
      if (dateRange.from) return `${format(dateRange.from, 'dd MMM', { locale: es })} - ...`;
      return 'Rango de fechas';
    }
    if (selectedDates.length > 0) {
      if (selectedDates.length === 1) return format(selectedDates[0], 'dd MMM yyyy', { locale: es });
      return `${selectedDates.length} fechas`;
    }
    return 'Selecciona fechas';
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="search-page">
      <div className="bg-white border-b shadow-sm sticky top-16 z-40">
        <div className="max-w-screen-2xl mx-auto px-4 py-3">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => { setActiveService(''); setFilteredProviders(providers); }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${!activeService ? 'bg-[#E6202E] text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              data-testid="service-tab-all"
            >
              Todos
            </button>

            {SERVICE_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveService(tab.id);
                  setDateRange({ from: undefined, to: undefined });
                  setSelectedDates([]);
                  if (searchAddress) {
                    filterProvidersByLocation(mapCenter, searchRadius);
                  } else {
                    setFilteredProviders(providers.filter(p => p.services?.some(s => s.service_type === tab.id)));
                  }
                }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${activeService === tab.id ? 'bg-[#E6202E] text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid={`service-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <div className="flex-1 relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Buscar por dirección o comuna..."
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                className="w-full pl-12 pr-10 h-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E6202E] focus:border-transparent"
                data-testid="search-input"
              />
              {searchAddress && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {activeService && (
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-3 h-12 border border-gray-200 rounded-xl hover:bg-gray-50 min-w-[160px]"
                    data-testid="search-date-trigger"
                  >
                    <Search className="w-4 h-4 text-gray-400" />
                    <span className={`text-sm truncate ${(dateRange.from || selectedDates.length > 0) ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                      {getDateLabel()}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-50" align="start">
                  {activeService === 'alojamiento' ? (
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={r => setDateRange(r || { from: undefined, to: undefined })}
                      locale={es}
                      numberOfMonths={2}
                      disabled={{ before: new Date() }}
                    />
                  ) : (
                    <Calendar
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={d => setSelectedDates(d || [])}
                      locale={es}
                      numberOfMonths={2}
                      disabled={{ before: new Date() }}
                    />
                  )}
                </PopoverContent>
              </Popover>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={getCurrentLocation}
              disabled={locationLoading}
              className="h-12 px-4 border-gray-200 hover:bg-gray-50"
              data-testid="location-button"
            >
              <Navigation className={`w-5 h-5 ${locationLoading ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline ml-2">Actualizar ubicación</span>
            </Button>

            <Button
              type="submit"
              className="h-12 px-6 bg-[#E6202E] hover:bg-[#D31522]"
              data-testid="search-submit"
            >
              <Search className="w-5 h-5" />
              <span className="hidden sm:inline ml-2">Buscar</span>
            </Button>
          </form>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row-reverse" style={{ height: 'calc(100vh - 128px)' }}>
        <div className="w-full lg:w-1/2 h-[400px] lg:h-full relative">
          {loadError ? (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
              <MapPin className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-500 font-medium">Mapa no disponible</p>
              <p className="text-gray-400 text-sm mt-1">Usa la lista para encontrar cuidadores</p>
            </div>
          ) : !isLoaded ? (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-[#E6202E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <GoogleMap
                onLoad={onMapLoad}
                center={mapCenter}
                zoom={mapZoom}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: false,
                  zoomControl: true,
                  mapTypeControl: false,
                  streetViewControl: false,
                  fullscreenControl: true
                }}
                onIdle={handleBoundsChanged}
              >
                {filteredProviders.map((provider) => (
                  provider.latitude && provider.longitude && (
                    <Marker
                      key={provider.provider_id}
                      position={{ lat: provider.latitude, lng: provider.longitude }}
                      icon={createMarkerIcon(
                        selectedProvider?.provider_id === provider.provider_id ||
                        hoveredProvider?.provider_id === provider.provider_id
                      )}
                      onClick={() => setSelectedProvider(provider)}
                      onMouseOver={() => setHoveredProvider(provider)}
                      onMouseOut={() => setHoveredProvider(null)}
                    />
                  )
                ))}

                {userLocation && (
                  <Marker
                    position={userLocation}
                    icon={createUserLocationIcon()}
                    title="Tu ubicación"
                  />
                )}

                {selectedProvider && (
                  <InfoWindow
                    position={{ lat: selectedProvider.latitude, lng: selectedProvider.longitude }}
                    onCloseClick={() => setSelectedProvider(null)}
                  >
                    <div className="p-2 max-w-[250px]">
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                          {getProviderMainImage(selectedProvider) ? (
                            <img
                              src={getProviderMainImage(selectedProvider)}
                              alt={firstNameOnly(selectedProvider.business_name)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <MapPin className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm text-gray-900 truncate">
                            {firstNameOnly(selectedProvider.business_name)}
                          </h3>
                          <p className="text-xs text-gray-500 truncate">{selectedProvider.comuna}</p>
                          {selectedProvider.rating > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                              <span className="text-xs font-medium">{selectedProvider.rating}</span>
                            </div>
                          )}
                          {selectedProvider.distance_km && (
                            <p className="text-xs text-[#E6202E] mt-1">
                              {selectedProvider.distance_km} km
                            </p>
                          )}
                        </div>
                      </div>

                      <Link
                        to={getProviderProfileUrl(selectedProvider.provider_id)}
                        className="mt-3 block w-full text-center py-2 bg-[#E6202E] text-white text-sm font-medium rounded-lg hover:bg-[#D31522]"
                      >
                        Ver perfil
                      </Link>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>

              <button
                onClick={() => setIsMapSearchActive(!isMapSearchActive)}
                className={`absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full shadow-lg font-medium text-sm transition-all ${
                  isMapSearchActive
                    ? 'bg-[#E6202E] text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
                data-testid="dynamic-search-toggle"
              >
                {isMapSearchActive ? (
                  <>
                    <span className="inline-block w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                    Búsqueda dinámica activa
                  </>
                ) : (
                  'Buscar al mover el mapa'
                )}
              </button>
            </>
          )}
        </div>

        <div className="w-full lg:w-1/2 h-full overflow-y-auto bg-white border-r">
          <div className="p-4 border-b bg-gray-50 sticky top-0 z-10">
            <h2 className="font-bold text-lg text-gray-900">
              {loading ? 'Buscando...' : `${filteredProviders.length} Cuidadores encontrados`}
            </h2>
            {searchAddress && !loading && (
              <p className="text-sm text-gray-500 mt-1">
                Cerca de: {searchAddress}
              </p>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-[#E6202E] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4">
              <MapPin className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-600 text-center mb-4">
                No se encontraron cuidadores en esta zona
              </p>
              <Button onClick={clearSearch} variant="outline">
                Ver todos los cuidadores
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredProviders.map((provider) => (
                <Link
                  key={provider.provider_id}
                  to={getProviderProfileUrl(provider.provider_id)}
                  className={`block p-4 hover:bg-gray-50 transition-colors ${
                    (hoveredProvider?.provider_id === provider.provider_id ||
                      selectedProvider?.provider_id === provider.provider_id)
                      ? 'bg-red-50' : ''
                  }`}
                  onMouseEnter={() => setHoveredProvider(provider)}
                  onMouseLeave={() => setHoveredProvider(null)}
                  data-testid="provider-card"
                >
                  <div className="flex gap-4">
                    <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200">
                      {getProviderMainImage(provider) ? (
                        <img
                          src={getProviderMainImage(provider)}
                          alt={firstNameOnly(provider.business_name)}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <MapPin className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 truncate">
                            {firstNameOnly(provider.business_name)}
                          </h3>

                          {formatLastSeen(provider.last_seen) && (
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                              <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                              <span>{formatLastSeen(provider.last_seen)}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {provider.verified && (
                            <Shield className="w-5 h-5 text-sky-500 fill-sky-500" data-testid="verified-badge" />
                          )}

                          {provider.is_subscribed && (
                            <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm" data-testid="premium-badge">
                              <Crown className="w-3 h-3" />Premium
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                        <MapPin className="w-4 h-4" />
                        <span className="truncate">{provider.comuna}</span>
                        {provider.distance_km && (
                          <span className="text-[#E6202E] font-medium whitespace-nowrap">
                            ({provider.distance_km} km)
                          </span>
                        )}
                      </div>

                      {provider.rating > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-semibold text-sm">{provider.rating}</span>
                          <span className="text-gray-400 text-sm">
                            ({provider.total_reviews} reseñas)
                          </span>
                        </div>
                      )}

                      {provider.services && provider.services.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {provider.services.slice(0, 3).map((service, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-red-50 text-[#E6202E] text-xs rounded-full font-medium capitalize"
                            >
                              {service.service_type}
                            </span>
                          ))}
                        </div>
                      )}

                      {provider.services?.some(s => s.pet_sizes?.length > 0) && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <Dog className="w-3.5 h-3.5 text-gray-400" />
                          <div className="flex gap-1">
                            {[...new Set(provider.services.flatMap(s => s.pet_sizes || []))].map((size, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-medium">
                                {PET_SIZE_LABELS[size] || size}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center">
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;